/**
 * Fixtures & tests: saved input / expected-output cases for the current model,
 * edited as the complete frozen fixture document (one lossless source of
 * truth), and run one at a time or all together against the current revision.
 */
import { useState } from 'react'
import { IconPlayerPlay, IconPlus, IconTrash } from '@tabler/icons-react'
import { parse, stringify } from 'lossless-json'
import {
  useCreateDealModelFixture,
  useDealModelFixtures,
  useDeleteDealModelFixture,
  usePatchDealModelFixture,
  useRunDealModelFixtures,
  type DealModelFixtureDocument,
  type DealModelSummary,
} from '../../api/dealModels'
import { useListMotion } from '../../theme/motion'
import { Alert, Button, Empty, Field, Pill, Skeletons, Textarea } from '../../ui/kit'

function newFixtureDocument(): DealModelFixtureDocument {
  return { id: crypto.randomUUID(), key: 'newFixture', label: 'New fixture', resolvedInputs: {}, scenarioIds: [], expected: { calculationState: 'computed', resultSubset: {}, missingInputIds: [], checks: [], verdictValue: null, diagnosticCodes: [] }, comparison: { mode: 'exact' } }
}

function fixtureSummary(document: DealModelFixtureDocument): string {
  const inputs = Object.keys(document.resolvedInputs).length
  return `${document.expected.calculationState} · ${inputs} resolved input root${inputs === 1 ? '' : 's'} · ${document.scenarioIds.length} scenario${document.scenarioIds.length === 1 ? '' : 's'} · ${document.comparison.mode}`
}

export function DealModelFixtures({ model }: { model: DealModelSummary }) {
  const fixtures = useDealModelFixtures(model.logicalModelId)
  const createFixture = useCreateDealModelFixture(model.logicalModelId)
  const patchFixture = usePatchDealModelFixture(model.logicalModelId)
  const deleteFixture = useDeleteDealModelFixture(model.logicalModelId)
  const runFixtures = useRunDealModelFixtures(model.logicalModelId)
  const [creating, setCreating] = useState(false)
  const [documentText, setDocumentText] = useState('')
  const [documentError, setDocumentError] = useState<string | null>(null)
  const [editingFixtureId, setEditingFixtureId] = useState<string | null>(null)
  const [listRef] = useListMotion<HTMLDivElement>()

  if (fixtures.isPending) return <div className="page dm-page"><Skeletons count={4} kind="row" /></div>
  if (fixtures.isError) return <Alert title="Fixtures unavailable">{fixtures.error.message}</Alert>
  const items = fixtures.data.items
  const revision = fixtures.data.revision ?? model.revision

  const submit = () => {
    let document: DealModelFixtureDocument
    try { document = parse(documentText) as DealModelFixtureDocument }
    catch (error) { setDocumentError(error instanceof Error ? error.message : 'Fixture document is invalid JSON.'); return }
    const input = { expectedRevision: revision, fixture: document }
    const done = () => { setDocumentText(''); setDocumentError(null); setEditingFixtureId(null); setCreating(false) }
    if (editingFixtureId) patchFixture.mutate({ fixtureId: editingFixtureId, ...input }, { onSuccess: done })
    else createFixture.mutate(input, { onSuccess: done })
  }

  return (
    <div className="page dm-page" data-testid="deal-model-fixtures">
      <div className="dm-section-head">
        <div>
          <h2 className="dm-title">Fixtures &amp; tests</h2>
          <p className="dm-note">Test the current model with saved input and expected output cases.</p>
        </div>
        <div className="dm-actions">
          <Button disabled={!model.capabilities.test} onClick={() => { setEditingFixtureId(null); setDocumentText(stringify(newFixtureDocument(), undefined, 2) ?? ''); setDocumentError(null); setCreating((value) => !value) }}><IconPlus className="ic-sm" />New fixture</Button>
          <Button kind="primary" busy={runFixtures.isPending && !runFixtures.variables?.fixtureId} disabled={!model.capabilities.test || items.length === 0} onClick={() => runFixtures.mutate({ revision, requestFingerprint: crypto.randomUUID() })}><IconPlayerPlay className="ic-sm" />Run all</Button>
        </div>
      </div>

      {creating ? (
        <section className="panel dm-panel" aria-label={editingFixtureId ? 'Edit fixture' : 'New fixture'}>
          <div className="panel-h"><h3 className="t-section">{editingFixtureId ? 'Edit fixture' : 'New fixture'}</h3></div>
          <p className="dm-note">Edit the complete frozen fixture document. Inputs, scenarios, expected evidence and comparison remain one lossless source of truth.</p>
          <Field label="Fixture document" error={documentError}>
            <Textarea mono rows={16} ariaLabel="Fixture document" value={documentText} onChange={(value) => { setDocumentText(value); setDocumentError(null) }} />
          </Field>
          <div className="form-actions">
            <Button onClick={() => { setCreating(false); setEditingFixtureId(null) }}>Cancel</Button>
            <Button kind="primary" busy={createFixture.isPending || patchFixture.isPending} disabled={!documentText.trim()} onClick={submit}>{editingFixtureId ? 'Save fixture' : 'Create fixture'}</Button>
          </div>
        </section>
      ) : null}

      {createFixture.isError ? <Alert tight title="Fixture was not created">{createFixture.error.message}</Alert> : null}
      {patchFixture.isError ? <Alert tight title="Fixture was not updated">{patchFixture.error.message}</Alert> : null}
      {deleteFixture.isError ? <Alert tight title="Fixture was not deleted">{deleteFixture.error.message}</Alert> : null}
      {runFixtures.isError ? <Alert tight title="Fixture run failed">{runFixtures.error.message}</Alert> : null}
      {runFixtures.data ? (
        runFixtures.data.failed === 0
          ? <Alert info tight title="Fixture run passed">{runFixtures.data.passed} passed · {runFixtures.data.failed} failed</Alert>
          : <Alert tight title="Fixture run failed">{runFixtures.data.passed} passed · {runFixtures.data.failed} failed</Alert>
      ) : null}

      {items.length === 0 ? (
        <Empty title="No fixtures yet">Create an expected input/output case to validate this model.</Empty>
      ) : (
        <div className="dm-fixture-list" ref={listRef}>
          {items.map((fixture) => (
            <article key={fixture.fixtureId} className="panel dm-fixture">
              <div className="dm-fixture-head">
                <div>
                  <h3 className="dm-fixture-name">{fixture.label}</h3>
                  <span className="dm-note t-mono">#{fixture.ordinal} · {fixture.key}</span>
                </div>
                <Pill muted>{fixture.document.expected.calculationState}</Pill>
              </div>
              <p className="dm-fixture-summary">{fixtureSummary(fixture.document)}</p>
              <div className="dm-actions is-end">
                <Button small disabled={!model.capabilities.test} onClick={() => { setEditingFixtureId(fixture.fixtureId); setDocumentText(stringify(fixture.document, undefined, 2) ?? ''); setDocumentError(null); setCreating(true) }}>Edit</Button>
                <Button small kind="danger" busy={deleteFixture.isPending && deleteFixture.variables?.fixtureId === fixture.fixtureId} disabled={!model.capabilities.test} onClick={() => deleteFixture.mutate({ fixtureId: fixture.fixtureId, expectedRevision: revision })}><IconTrash className="ic-sm" />Delete</Button>
                <Button small busy={runFixtures.isPending && runFixtures.variables?.fixtureId === fixture.fixtureId} disabled={!model.capabilities.test} onClick={() => runFixtures.mutate({ fixtureId: fixture.fixtureId, revision, requestFingerprint: crypto.randomUUID() })}><IconPlayerPlay className="ic-sm" />Run</Button>
              </div>
            </article>
          ))}
        </div>
      )}
    </div>
  )
}
